import api from "api/api";
import {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useReducer,
  useState
} from "react";
import { getToken, hasSession, removeToken, setSession } from "Session";
import {
  ContextProps,
  LoginUserDataProps,
  User,
  UserActions
} from "./types/application-context-types";
import { Router, useRouter } from "router-manager/parts";
import { ModalErrorOptions } from "components/modal-error";
import { checkPermission } from "router-manager/parts";
import apiCarteira from "api/api-carteira";
import { ServicesProps } from "components/servicos-components/types";
import { apiJWT } from "api/api-jwt";
import { useEventListener } from "services/events";
import { userTypeRedirect } from "components/login-page/functions";
import { autoLogin } from "./attendance-context/utils";

const initialValues: ContextProps = {
  setUserAction: () => undefined,
  user: { isLogged: false, userData: {}, isLogging: true },
  setMenuErrorMessage: () => undefined,
  menuErrorMessage: undefined
};

const applicationContext = createContext<ContextProps>(initialValues);

const initialState = {
  isLogged: false,
  isLogging: true,
  userData: {}
};

function reducer(state: User, action: UserActions) {
  switch (action.type) {
    case "login":
      const { token, userData } = action.payload;
      setSession(token);
      return { ...state, userData, isLogging: false, isLogged: true };
    case "logout":
      removeToken();
      apiJWT.deleteTokens();
      return {
        ...state,
        userData: {},
        isLogging: false,
        isLogged: false
      };
    case "logging":
      return { ...state, isLogging: action.payload };
    default:
      return state;
  }
}

export const ApplicationContextProvider = ({
  children
}: {
  children: ReactNode;
}) => {
  const [user, setUserAction] = useReducer(reducer, initialState);
  const [menuErrorMessage, setMenuErrorMessage] = useState<ModalErrorOptions>();
  const router = useRouter();

  const currentRoute = Router.getRoute(router.pathname);

  useEventListener("logout", () => {
    setUserAction({ type: "logout" });
  });

  useEffect(() => {
    const thisRouter =
      window.location.pathname !== "/login" ? window.location.pathname : null;

    if (!hasSession()) {
      const isPermited = currentRoute?.checkPermission(user);
      if (!isPermited) {
        router.push({ pathname: "/login", payload: { prevPath: thisRouter } });
      }
    } else {
      autoLogin({ setUserAction, router, thisRouter });
    }
  }, []);

  const servicesRequest = async () => {
    const token = getToken();
    try {
      const servicos = await apiCarteira.get<ServicesProps>(
        `/services?customer_id=${user.userData.customerId}&servico=IA_VENDEAI`
      );
      setUserAction({
        type: "login",
        payload: {
          token: token!,
          userData: { ...user.userData, servicos: servicos?.data }
        }
      });
    } catch (e) {}
  };

  useEffect(() => {
    const permittedUser = checkPermission(
      user,
      ["SUPER", "COMERCIAL", "PARCEIRO_MASTER"],
      ["SUPER", "COMERCIAL", "PARCEIRO_MASTER"]
    );
    if (user.isLogged && permittedUser) servicesRequest();

    if (user.isLogged && user.userData.accessBlocked) {
      router.push(userTypeRedirect(user));
    }
  }, [user.isLogged]);

  return (
    <applicationContext.Provider
      value={{
        setUserAction,
        user,
        menuErrorMessage,
        setMenuErrorMessage
      }}
    >
      {children}
    </applicationContext.Provider>
  );
};

export const useApplicationContext = () => {
  const context = useContext(applicationContext);
  if (context === undefined)
    throw new Error(
      "useApplicationContext must be used within a ApplicationContextProvider"
    );
  return context;
};
